import { fetchGet } from "./CommonServiceUtils";
import { Constants } from "@/constants";
import {
  AccessModule,
  ApiPermission
} from "@shoutout-labs/market_buzz_crm_types";

export interface UserPermissions {
  modules: AccessModule[];
  apiPermissions: ApiPermission[];
  features?: string[];
  appType?: "ANALYTICS" | "CRM";
  meta?: {
    userId: string;
    organizationId: string;
    role: string;
    packageId: string;
    cacheHit: boolean;
    generatedAt: string;
  };
}

/**
 * Service for fetching user permissions and module access control
 */
class PermissionsService {
  /**
   * Fetch the current user's effective permissions
   * Returns the intersection of subscription package and role permissions
   */
  static async getCurrentUserPermissions(): Promise<UserPermissions> {
    const response = await fetchGet<{
      modules: string[];
      apiPermissions: string[];
      features?: string[];
      appType?: "ANALYTICS" | "CRM";
      meta?: {
        userId: string;
        organizationId: string;
        role: string;
        packageId: string;
        cacheHit: boolean;
        generatedAt: string;
      };
    }>(`${Constants.REACT_APP_API_BASE_URL}permissions/current`);

    // Transform string arrays to enum arrays
    const transformedResponse: UserPermissions = {
      modules: response.modules.map((module) => module as AccessModule),
      apiPermissions: response.apiPermissions.map(
        (permission) => permission as ApiPermission
      ),
      features: response.features,
      appType: response.appType,
      meta: response.meta
    };

    return transformedResponse;
  }

  /**
   * Check if the current user has access to a specific module
   * @param requiredModule The module to check access for
   * @returns Promise<boolean> True if user has access, false otherwise
   */
  static async hasModuleAccess(requiredModule: AccessModule): Promise<boolean> {
    try {
      const permissions = await this.getCurrentUserPermissions();
      return permissions.modules.includes(requiredModule);
    } catch (error) {
      console.error(
        `Failed to check access for module ${requiredModule}:`,
        error
      );
      return false;
    }
  }

  /**
   * Check if the current user has a specific API permission
   * @param requiredPermission The API permission to check
   * @returns Promise<boolean> True if user has permission, false otherwise
   */
  static async hasApiPermission(
    requiredPermission: ApiPermission
  ): Promise<boolean> {
    try {
      const permissions = await this.getCurrentUserPermissions();
      return permissions.apiPermissions.includes(requiredPermission);
    } catch (error) {
      console.error(
        `Failed to check API permission ${requiredPermission}:`,
        error
      );
      return false;
    }
  }

  /**
   * Get all features the current user has access to
   * @returns Promise<string[]> Array of feature strings the user has access to
   */
  static async getFeatures(): Promise<string[]> {
    try {
      const permissions = await this.getCurrentUserPermissions();
      return permissions.features || [];
    } catch (error) {
      console.error("Failed to get user features:", error);
      return [];
    }
  }

  /**
   * Check if the current user has access to a specific feature
   * @param requiredFeature The feature to check access for
   * @returns Promise<boolean> True if user has access, false otherwise
   */
  static async hasFeatureAccess(requiredFeature: string): Promise<boolean> {
    try {
      const permissions = await this.getCurrentUserPermissions();
      return permissions.features?.includes(requiredFeature) || false;
    } catch (error) {
      console.error(
        `Failed to check feature access for ${requiredFeature}:`,
        error
      );
      return false;
    }
  }
}

export default PermissionsService;
